<template>
	<div class="title-list" :class="{ 'title-list--CLS-block': isMounted && (!hasNoTitles || showFallbackTitles) }">
		<div class="container-fluid container-max-width">
			<slot name="list-options" />
			<slot name="spinner" />
		</div>

		<!-- ROW -->
		<div class="container-fluid container-max-width" v-if="!loading">
			<FallbackTitles
				v-if="!loading && showFallbackTitles"
				:collectionType="collectionType"
				@onPosterClick="$emit('onPosterClick', $event)"
				@mounted="$emit('show-fallbacks')"
			/>

			<component
				v-else
				ref="titleList"
				:is="listLayoutComponent"
				v-bind="listLayoutProps"
				@load-next="$emit('load-next', $event)"
				@load-prev="$emit('load-prev', $event)"
				@onPosterClick="$emit('onPosterClick', $event)"
				@removeTitleFromList="$emit('removeTitleFromList', $event)"
			>
				<template #sponsoredRecommendationHeader>
					<slot name="sponsoredRecommendationHeader"></slot>
				</template>

				<template v-if="!hasNoTitles" #sponsoredRecommendation>
					<ClientOnly>
						<slot name="sponsoredRecommendation"></slot>
					</ClientOnly>
				</template>

				<template v-if="!hasNoTitles" #trendingTitles>
					<ClientOnly>
						<slot name="trendingTitles"></slot>
					</ClientOnly>
				</template>

				<template #qualityTvRow>
					<slot name="qualityTvRow" />
				</template>
			</component>

			<TimelineEmptyState
				class="title-list--CLS-block"
				v-if="!loading && showEmptyStateContent && !showFallbackTitles"
				:hasNoTitles="hasNoTitles"
				:isAtEndOfList="isAtEndOfList"
				@onScrollToTop="$emit('onScrollToTop')"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue';

import { CollectionType } from '@/enums/collection-type';
import { TitleListLayout } from '@/enums/title-list-layout';

import type { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import type { PopularTitleGraphqlFragment } from '@/pages/graphql/fragments/PopularTitle.fragment';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';

import { getActiveFilters } from '@/helpers/filter-helper';
import { getDefaultFilterCollection, type FilterCollection } from '@/stores/useFilterStoreTypes';

import type TitleListGridType from '@/components/list/TitleListGrid.vue';
import { LoadMorePayload } from '@/helpers/composables/useBiScroll';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import { useMounted } from '@vueuse/core';
import { PaginationLevel } from '@/stores/pinia/useMetaStore';

const FallbackTitles = defineAsyncComponent(
	() => import(/* webpackChunkName: "timeline-fallback-titles" */ '@/components/timeline/FallbackTitles.vue')
);
const TimelineEmptyState = defineAsyncComponent(
	() => import(/* webpackChunkName: "timeline-empty-state" */ '@/components/timeline/TimelineEmptyState.vue')
);
const TitleListRow = defineAsyncComponent(
	() => import(/* webpackChunkName: "title-list-row" */ '@/components/list/TitleListRow.vue')
);
const TitleListGrid = defineAsyncComponent(
	() => import(/* webpackChunkName: "title-list-grid" */ '@/components/list/TitleListGrid.vue')
);
const TitleListCard = defineAsyncComponent(
	() => import(/* webpackChunkName: "title-list-card" */ '@/components/list/TitleListCard.vue')
);

const props = withDefaults(
	defineProps<{
		titlesAmount: number;
		collectionType: CollectionType;
		titles: PopularTitleGraphqlFragment[];
		titlesPerPage?: number;
		listLayout: TitleListLayout;
		isAtEndOfList?: boolean;
		isFirstPage?: boolean;
		isSeoSRLoaded?: boolean;
		hasActiveFilter?: boolean;
		currentFilters?: FilterCollection;
		sponsoredAd?: SponsoredAdFragment | undefined;
		paginationLevel?: PaginationLevel;
		loading?: boolean;
		placement?: ClickoutPlacement;
	}>(),
	{
		isAtEndOfList: false,
		isFirstPage: true,
		isSeoSRLoaded: false,
		hasActiveFilter: false,
		currentFilters: getDefaultFilterCollection,
		sponsoredAd: undefined,
		paginationLevel: 0,
		loading: false,
	}
);

useProvideClickoutPlacement(props.placement);

defineEmits<{
	(e: 'onPosterClick', payload: TitleEventPayloadGraphql): void;
	(e: 'removeTitleFromList', titleObjectId: number): void;
	(e: 'load-next', payload: LoadMorePayload): void;
	(e: 'load-prev', payload: LoadMorePayload): void;
	(e: 'onScrollToTop'): void;
	(e: 'show-fallbacks'): void;
}>();

const isMounted = useMounted();

const hasNoTitles = computed<boolean>(() => (props.titles.length || props.titlesAmount) === 0);

const showEmptyStateContent = computed<boolean>(() => {
	if (props.collectionType === CollectionType.TITLELIST) return false;
	if (props.hasActiveFilter) return true;

	const isTimelinePage = [CollectionType.NEW, CollectionType.POPULAR].includes(props.collectionType);

	// isAtEndOfTimeline
	return isTimelinePage && (props.isAtEndOfList || hasNoTitles.value);
});

const listLayoutProps = computed(() => {
	return {
		hasNoTitles: hasNoTitles.value,
		isAtEndOfList: props.isAtEndOfList,
		isFirstPage: props.isFirstPage,
		isSeoSRLoaded: props.isSeoSRLoaded,
		listLayout: props.listLayout,
		loading: props.loading,
		sponsoredAd: props.sponsoredAd,
		titles: props.titles,
		titlesAmount: props.titlesAmount,
		titlesPerPage: props.titlesPerPage,
		collectionType: props.collectionType,
		paginationLevel: props.paginationLevel,
	};
});

const collectionTypeToListLayout = {
	[TitleListLayout.ROW]: TitleListRow,
	[TitleListLayout.GRID]: TitleListGrid,
	[TitleListLayout.CARD]: TitleListCard,
};
const listLayoutComponent = computed(() => collectionTypeToListLayout[props.listLayout]);

const showFallbackTitles = computed(() => {
	const activeFilters = getActiveFilters(Object.keys(props.currentFilters) as any, props.currentFilters);

	return (
		activeFilters.providers &&
		Object.keys(activeFilters).every(filter => ['content_types', 'providers'].includes(filter)) &&
		hasNoTitles.value
	);
});

const titleList = ref<InstanceType<typeof TitleListGridType> | null>(null);

function resetInfiniteScroll() {
	titleList.value?.resetInfiniteScroll();
}

defineExpose({ resetInfiniteScroll });
</script>

<style scoped lang="scss">
@import '~@/styles/variables.scss';

.title-list {
	&--CLS-block {
		min-height: 100vh;
	}
}
</style>
